import {useState, useEffect} from 'react'
import {useBreakpointValue} from '@chakra-ui/react'
import isMobile, {isMobileResult} from 'ismobilejs'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import themeBreakpoints from '../../theme/foundations/breakpoints'

type Viewport = {
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
}

/**
 * @name useViewport
 *
 * @hook
 *
 * @description
 * Returns the viewport of the device based on user agent and breakpoints.
 *
 * @remarks
 * During the initial render, it uses the `isMobile` library to determine the device type.
 * After the initial render, it switches to using Chakra UI's `useBreakpointValue` to determine the viewport based on breakpoints.
 *
 * @returns {Viewport} - The viewport of the device, including whether it is mobile, tablet, or desktop.
 */
const useViewport = (): Viewport => {
    const {req} = useServerContext()
    const userAgent = req?.headers['user-agent']

    const [isClient, setIsClient] = useState(false)
    const data: isMobileResult = isMobile(userAgent)
    const breakpoints = Object.keys(themeBreakpoints.breakpoints)
    const responsiveValue = useBreakpointValue(
        breakpoints.reduce(
            (acc, key) => {
                acc[key] = key
                return acc
            },
            {} as Record<string, string>,
        ),
    )

    let isMobileDevice: boolean, isTabletDevice: boolean, isDesktopDevice: boolean

    if (isClient) {
        isMobileDevice = responsiveValue === 'base' || responsiveValue === 'sm'
        isTabletDevice = responsiveValue === 'md'
        isDesktopDevice = !isMobileDevice && !isTabletDevice
    } else {
        isMobileDevice = data.phone
        isTabletDevice = data.tablet
        isDesktopDevice = !data.phone && !data.tablet
    }

    useEffect(() => {
        setIsClient(true)
    }, [])

    return {
        isMobile: isMobileDevice,
        isTablet: isTabletDevice,
        isDesktop: isDesktopDevice,
    }
}

export {useViewport}
